"use client"

import { createContext, useContext, type ReactNode } from "react"

/**
 * Bridges the slotted "Your feedback" panel and the widget shell: the panel calls
 * this setter when a conversation opens/closes, and the shell (which owns the
 * width) widens into a two-pane layout while it is true. The shell provides the
 * setter; the panel consumes it. Default is a no-op so the panel still works if
 * ever rendered without a shell.
 */
export const FeedbackPanelExpansionContext = createContext<(expanded: boolean) => void>(() => {})

export function useFeedbackPanelExpansion(): (expanded: boolean) => void {
  return useContext(FeedbackPanelExpansionContext)
}

/**
 * Second bridge in the same direction: the panel hands the shell the identity of
 * the open conversation (area label, status, back control) so the shell can render
 * it inside its OWN header row instead of the panel spending a second stacked bar
 * on it. The shell can't build that node itself — the thread data lives in the
 * feature — so the feature builds it and the entity just renders it, same slot
 * trick as `yourFeedbackContent`. Pass null to clear.
 */
export const FeedbackPanelHeaderContext = createContext<(node: ReactNode | null) => void>(() => {})

export function useFeedbackPanelHeader(): (node: ReactNode | null) => void {
  return useContext(FeedbackPanelHeaderContext)
}

/**
 * Third bridge, and the only one pointing DOWN: the shell tells the panel that
 * this particular open should land straight in the newest unread conversation.
 * The shell owns the tab and knows the unread count; the panel owns the list and
 * the selection, so neither can decide alone.
 *
 * Why a flag and not just "auto-open whenever something is unread": the panel
 * mounts on the first visit to "Your feedback" and then STAYS mounted (hidden)
 * on the give tab for the rest of the widget's life. Without an explicit signal,
 * a reply arriving on the 30s poll while the user sat on "Give feedback" would
 * silently select that ticket and mark it read behind a hidden pane. The shell
 * only raises this on an open transition that already had unread, and drops it
 * on close.
 */
export const FeedbackPanelAutoOpenContext = createContext<boolean>(false)

export function useFeedbackPanelAutoOpen(): boolean {
  return useContext(FeedbackPanelAutoOpenContext)
}
